import { regionLabel } from '@/utils/regionLabel';
import React from 'react';
import styled from '@emotion/styled';
import { filterTokens } from '@/components/common/filter';
import FitOneLine from '@/components/common/FitOneLine';

/**
 * 채용 공고 요약 공용 조각(2026-09-30 사용자 결정 — 카드 대안 A, "홈 프리미엄도 텍스트형도 다 맞춰").
 * 공고 요약이 보이는 모든 곳이 같은 순서: 제목 → 근무 요일(청록 굵게) → "지역 · 직무 · 근무형태".
 * 근무 요일·요약은 한 줄 고정(글자 자동 축소 → 12px 후 말줄임). 제목은 최대 두 줄이고 제목 아래 간격은 고정(2026-10-01, UI 가이드 6.1).
 * 쓰는 곳: 채용 목록·홈 추천 카드(RecruitmentGrid), 홈 프리미엄(CardListCarousel), 홈 지역 목록(RecruitmentRowList),
 * 통합 검색 채용 결과, 치과 공고 관리·지원자 관리, 이전 공고 불러오기.
 */

/** "월~금"처럼 물결로 이어진 말이 물결 앞뒤에서 줄바뀌지 않게 — 물결 양옆에 줄바꿈 없는 연결 문자(U+2060)를 넣는다 */
export const keepTildeTogether = (text: string): string => text.replace(/(\S)~(?=\S)/g, '$1\u2060~\u2060');

/**
 * 요약 줄 글 — 지역 · 직무 · 근무형태. 여러 개면 withRest 일 때 "진료실 외 1개", 아니면 첫 것만(좁은 폰 카드).
 * 좁은 폰 카드(withRest=false)에서 구가 있는 시("천안시 서북구")는 시·도를 빼 "천안시 서북구"만 — 한 줄에 되도록 들어가게.
 * 지역이 없으면(지역을 모르는 화면) 직무 · 근무형태만.
 */
export const summaryText = (city: string | undefined, district: string | undefined, jobTasks: string[] | undefined, recruitmentTypes: string[] | undefined, withRest: boolean): string => {
    const first = (list: string[] | undefined): string => {
        if (!list || list.length === 0) return '미지정';
        // "아르바이트 외 1개"가 "외 / 1개"로 끊기지 않게 붙인다
        return withRest && list.length > 1 ? `${list[0]}\u00A0외\u00A0${list.length - 1}개` : list[0];
    };
    const districtText: string = (district ?? '').trim();
    const region: string = !withRest && districtText.includes(' ') ? districtText : regionLabel(city, districtText);
    return [region, first(jobTasks), first(recruitmentTypes)].filter(Boolean).join(' · ');
};

/** 근무 요일 줄 높이 비율 — 줄 높이는 기본 글자 크기 기준 고정(글자가 줄어도 카드 높이 불변) */
export const WORK_DAYS_LINE_RATIO: number = 1.35;
/** 요약 줄 높이 비율 */
export const SUMMARY_LINE_RATIO: number = 1.4;

/** 근무 요일 기본 글자 크기(화면마다 size 로 바꿀 수 있음) */
export const workDaysBaseSize = (pc?: boolean, size?: string): string => size ?? (pc ? '0.84rem' : '0.78rem');
/** 요약 기본 글자 크기 */
export const summaryBaseSize = (size?: string): string => size ?? '12px';

const WorkDaysBox = styled(FitOneLine, { shouldForwardProp: (prop: string) => prop !== 'fontSize' })<{ fontSize: string }>`
    flex-shrink: 0;
    font-size: ${({ fontSize }) => fontSize};
    line-height: calc(${({ fontSize }) => fontSize} * ${WORK_DAYS_LINE_RATIO});
    height: calc(${({ fontSize }) => fontSize} * ${WORK_DAYS_LINE_RATIO});
    font-weight: 700;
    color: ${filterTokens.color.selectedText};
`;

const SummaryBox = styled(FitOneLine, { shouldForwardProp: (prop: string) => prop !== 'fontSize' })<{ fontSize: string }>`
    flex-shrink: 0;
    font-size: ${({ fontSize }) => fontSize};
    line-height: calc(${({ fontSize }) => fontSize} * ${SUMMARY_LINE_RATIO});
    height: calc(${({ fontSize }) => fontSize} * ${SUMMARY_LINE_RATIO});
    color: ${filterTokens.color.textMuted};
`;

export interface LineProps {
    pc?: boolean;
    /** 기본 글자 크기(화면마다 다름) — 넘치면 12px 까지 줄이고 그래도 넘치면 말줄임 */
    size?: string;
    className?: string;
}

/**
 * 근무 요일 한 줄 — 청록 굵게, 모든 카드에서 한 줄 고정(2026-09-30 사용자 결정).
 * 넘치면 글자를 줄여 맞추고, 12px 에서도 넘칠 때만 말줄임(전체 글은 title).
 */
export const WorkDaysLine = ({ label, pc, size, className }: LineProps & { label: string }) => (
    <WorkDaysBox text={label} fontSize={workDaysBaseSize(pc, size)} className={className} line="workdays" />
);

/** 요약 "지역 · 직무 · 근무형태" 한 줄 — 근무 요일과 같은 규칙(글자 축소 → 12px 후 말줄임) */
export const SummaryLine = ({ text, size, className }: LineProps & { text: string }) => (
    <SummaryBox text={text} fontSize={summaryBaseSize(size)} className={className} line="summary" />
);

/** 근무 요일 줄 높이(CSS 길이) — 카드 높이 계산용 */
export const workDaysLineHeight = (pc?: boolean, size?: string): string => `(${workDaysBaseSize(pc, size)} * ${WORK_DAYS_LINE_RATIO})`;
/** 요약 줄 높이(CSS 길이) — 카드 높이 계산용 */
export const summaryLineHeight = (size?: string): string => `(${summaryBaseSize(size)} * ${SUMMARY_LINE_RATIO})`;

export interface RecruitmentSummaryLinesProps {
    workDaysLabel?: string | null;
    city?: string;
    district?: string;
    jobTasks?: string[];
    recruitmentTypes?: string[];
    /** 여러 직무·근무형태를 "외 N개"로(넓은 칸) */
    withRest?: boolean;
    pc?: boolean;
    workDaysSize?: string;
    summarySize?: string;
}

/** 제목 아래 두 줄 — 근무 요일(없으면 그리지 않음) + 요약, 둘 다 한 줄 고정 */
export const RecruitmentSummaryLines = ({ workDaysLabel, city, district, jobTasks, recruitmentTypes, withRest = false, pc, workDaysSize, summarySize }: RecruitmentSummaryLinesProps) => (
    <>
        {workDaysLabel?.trim() && <WorkDaysLine label={workDaysLabel.trim()} pc={pc} size={workDaysSize} />}
        <SummaryLine text={summaryText(city, district, jobTasks, recruitmentTypes, withRest)} pc={pc} size={summarySize} />
    </>
);
